import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Search, ArrowLeft, Loader2, Calendar, ArrowRight, BookOpen } from 'lucide-react';
import { LanguageDropdown, ALL_LANG_MAP } from '../components/LanguageDropdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogListing = () => {
  const navigate = useNavigate();
  const { lang: langParam, langOrSlug } = useParams();
  // langOrSlug is used when /blog/:langOrSlug (listing with lang code)
  const langFromUrl = langParam || langOrSlug;
  const activeLang = langFromUrl && ALL_LANG_MAP[langFromUrl] ? langFromUrl : 'en';

  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    document.title = activeLang === 'en' ? 'Blog - Primeswift Solutions' : `Blog (${ALL_LANG_MAP[activeLang]?.native || ''}) - Primeswift Solutions`;
    fetchCategories();
    fetchBlogs();
    return () => { document.title = 'Primeswift Solutions Pvt Ltd'; };
  }, [activeLang]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/blogs/categories`);
      setCategories(res.data);
    } catch { /* silently fail */ }
  };

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = activeLang !== 'en' ? { lang: activeLang } : {};
      const res = await axios.get(`${API}/blogs`, { params });
      setBlogs(res.data);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  };

  const filtered = blogs.filter(b => {
    const matchCategory = !activeCategory || b.category === activeCategory;
    const matchSearch = !search ||
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      (b.excerpt || '').toLowerCase().includes(search.toLowerCase()) ||
      b.category.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

  const getBlogUrl = (slug) => activeLang === 'en' ? `/blog/${slug}` : `/blog/${activeLang}/${slug}`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO hreflang */}
      <HreflangListing />

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
          <div className="flex items-center justify-between mb-4">
            <Button data-testid="blog-back-home" variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white hover:bg-white/10 -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" />Home
            </Button>
            <LanguageDropdown activeLang={activeLang} basePath="/blog" variant="dark" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="blog-page-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Blog</h1>
          <p className="text-slate-300 text-base md:text-lg">Insights, news & updates from Primeswift Solutions</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <Card className="border-slate-100 shadow-lg">
          <CardContent className="pt-6">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                data-testid="blog-search"
                placeholder="Search articles..."
                className="pl-10 h-12 bg-slate-50"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="filter-all"
                onClick={() => setActiveCategory('')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!activeCategory ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`filter-${cat}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Blog Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-600">Loading articles...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No articles found</h3>
            <p className="text-slate-500">Check back later for new content</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">{filtered.length} article{filtered.length !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(blog => (
                <Card
                  key={blog.id}
                  data-testid={`blog-card-${blog.slug}`}
                  className="border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => navigate(getBlogUrl(blog.slug))}
                >
                  {blog.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img src={blog.featured_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <CardContent className={`${blog.featured_image ? 'pt-4' : 'pt-6'} space-y-3`}>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 font-medium">{blog.category}</Badge>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />{formatDate(blog.created_at)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2">{blog.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-3 leading-relaxed">{blog.excerpt}</p>
                    <div className="flex items-center text-blue-600 text-sm font-semibold pt-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const HreflangListing = () => {
  useEffect(() => {
    const links = [
      { rel: 'alternate', hreflang: 'en-IN', href: 'https://primeswift.in/blog' },
      { rel: 'alternate', hreflang: 'x-default', href: 'https://primeswift.in/blog' },
      { rel: 'alternate', hreflang: 'hi-IN', href: 'https://primeswift.in/blog/hi' },
      { rel: 'alternate', hreflang: 'mr-IN', href: 'https://primeswift.in/blog/mr' },
    ];
    const els = links.map(l => {
      const el = document.createElement('link');
      Object.entries(l).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      return el;
    });
    return () => els.forEach(el => el.remove());
  }, []);
  return null;
};

export default BlogListing;
